import {AbstractControl, AsyncValidatorFn, ValidationErrors} from '@angular/forms';

import {map, switchMap} from 'rxjs/operators';
import {
    BusinessType,
    PublicService,
    QrCodeValidateDescription,
    Response,
    WebAccountService,
    WebQRService,
    WebUserService
} from 'web-angular-client';
import {Observable, timer} from 'rxjs';
import {ValidationHelper} from './validation.helper';
import ResponseEnum = Response.ResponseCodeEnum;

/**
 * Defines a set of common asynchronous validators for use with form controls. Asynchronous validators perform web
 * service calls to validate form input.
 */
export class AsyncValidators
{
    /**
     * Used to validate that the registered business name entered into a form control is unique.
     *
     * @return ```typescript {duplicateBusinessName: true}
     * ```
     * if the business name already exists.
     */
    static validateBusinessName(publicService: PublicService)
    {
        return (control: AbstractControl) =>
        {
            return timer(1000).pipe(switchMap(() =>
            {
                return publicService.validateBusinessExists(
                    control.value, null).pipe(map((res: Response) =>
                {
                    return res.responseCode === ResponseEnum.TRUE
                        ?
                        {duplicateBusinessName: true} :
                        null;
                }));
            }));
        };
    }

    /**
     * Used to validate that the business registration number entered into a form control is unique.
     *
     * @return ``` typescript
     * {duplicateBusinessRegistration: true}
     * ```
     * if the business registration number already exists.
     */
    static validateBusinessRegistrationNumber(
        publicService: PublicService,
        businessType: BusinessType,
        integratorPrefix: string = null)
    {
        return (control: AbstractControl) =>
        {

            return timer(1000).pipe(switchMap(() =>
            {
                const registrationNumberValidation = {
                    registrationNumber: control.value?.replace(/\s+/g, '') ?? null,
                    integratorPrefix: integratorPrefix ?? null,
                    businessType: businessType
                };

                return publicService.validateRegistrationNumberExists(
                    registrationNumberValidation).pipe(map((res: Response) =>
                {
                    return res.responseCode === ResponseEnum.FOUND
                        ?
                        ValidationHelper.getCustomValidatorResponse(
                            'backendValidation',
                            'A business with this registration number already exists.') :
                        null;
                }));
            }));
        };
    }

    /**
     * Used to validate that the user email address entered into a form control is unique.
     *
     * @return ```typescript
     * {duplicateEmail: true}
     * ```
     * if the user email address already exists.
     */
    static validateUserEmail(publicService: PublicService)
    {
        return (control: AbstractControl) =>
        {
            return timer(1000).pipe(switchMap(() =>
            {
                return publicService.validateUserExists(encodeURIComponent(control.value)).pipe(map((res: Response) =>
                {
                    return res.responseCode === ResponseEnum.TRUE
                        ?
                        {duplicateEmail: true} :
                        null;
                }));
            }));
        };
    }

    /**
     * Used to validate that the Account Number entered into a form control is unique.
     *
     * @return ```typescript
     * {duplicateAccountNumber: true}
     * ```
     *if the Account Number already exists.
     */
    static validateAccountNumber(accountService: WebAccountService): AsyncValidatorFn
    {
        return (control: AbstractControl): Observable<ValidationErrors> =>
        {
            return timer(1000).pipe(switchMap(() =>
            {
                return accountService
                    .validateAccountNumber(control.value)
                    .pipe(map((res: Response) =>
                    {
                        return res.responseCode === ResponseEnum.FOUND
                            ?
                            {duplicateAccountNumber: true} :
                            null;
                    }));
            }));
        };
    }

    /**
     * Used to validate that the QR Code description entered into a form control is unique.
     *
     * @return ```typescript
     * {duplicateQrDescription: true}
     * ```
     *if the QR Code description already exists.
     */
    static validateQrCodeDescription(webQrService: WebQRService, qrCodeId: number = null): AsyncValidatorFn
    {
        return (control: AbstractControl): Observable<ValidationErrors> =>
        {
            return timer(400).pipe(switchMap(() =>
            {
                const qrCodeDescriptionValidation: QrCodeValidateDescription = {
                    accountId: qrCodeId,
                    description: control.value
                };
                return webQrService
                    .validateQrDescriptionExists(qrCodeDescriptionValidation)
                    .pipe(map((res: Response) =>
                    {
                        return res.responseCode === ResponseEnum.FOUND
                            ?
                            ValidationHelper.getCustomValidatorResponse(
                                'backendValidation',
                                ' QR code description already exists for this business') :
                            null;
                    }));
            }));
        };
    }

    /**
     * Used to validate that the user email entered into a form control is unique for the given business.
     *
     * @return ```typescript
     * {backendValidation: true}
     * ```
     *if the user email already exists for the given business.
     */
    static validateUserForBusinessExists(webUserService: WebUserService): AsyncValidatorFn
    {
        return (control: AbstractControl): Observable<ValidationErrors> =>
        {
            return timer(400).pipe(switchMap(() =>
            {
                return webUserService.validateUserLoginForBusinessExists(control.value)
                    .pipe(map((res: Response) =>
                    {
                        return res.responseCode === ResponseEnum.TRUE
                            ?
                            ValidationHelper.getCustomValidatorResponse(
                                'backendValidation',
                                ' User with this email address already exists for this business') :
                            null;
                    }));
            }));
        };
    }
}